import { useState } from 'react';
import { Star, MessageSquare, ThumbsUp, Search, Filter, TrendingUp } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';

interface Review {
  id: number;
  customer: string;
  rating: number;
  comment: string;
  orderNumber: string;
  date: string;
  reply?: string;
  helpful: number;
}

const mockReviews: Review[] = [
  { id: 1, customer: 'أحمد محمد', rating: 5, comment: 'قهوة ممتازة والتوصيل سريع جداً. أفضل كابتشينو في المنطقة!', orderNumber: '#1025', date: '2026-03-22T10:30:00', reply: 'شكراً لك أحمد! سعداء بخدمتك 😊', helpful: 12 },
  { id: 2, customer: 'سارة علي', rating: 4, comment: 'المشروبات ممتازة لكن التوصيل تأخر قليلاً. الطعم رائع كالعادة.', orderNumber: '#1019', date: '2026-03-21T21:30:00', helpful: 8 },
  { id: 3, customer: 'محمود حسن', rating: 5, comment: 'الموكا الساخنة كانت مذهلة! والتيراميسو لذيذ جداً. سأطلب مرة أخرى بالتأكيد.', orderNumber: '#1023', date: '2026-03-21T15:00:00', reply: 'شكراً محمود! ننتظرك دائماً ☕', helpful: 15 },
  { id: 4, customer: 'فاطمة أحمد', rating: 3, comment: 'الطلب وصل بارد شوية. أتمنى تحسين التغليف الحراري.', orderNumber: '#1022', date: '2026-03-21T08:30:00', helpful: 5 },
  { id: 5, customer: 'مريم عادل', rating: 5, comment: 'من أفضل المقاهي! الكابتشينو والبان كيك كانوا مثاليين. خدمة ممتازة.', orderNumber: '#1016', date: '2026-03-20T18:15:00', reply: 'يسعدنا رأيك مريم! نتمنى لك تجربة دائمة ممتعة 🌹', helpful: 20 },
  { id: 6, customer: 'كريم سعد', rating: 4, comment: 'القهوة اللي عندكم مميزة. بس ياريت تضيفوا خيارات أكتر للحليب النباتي.', orderNumber: '#1017', date: '2026-03-20T14:00:00', helpful: 7 },
  { id: 7, customer: 'يوسف عبدالله', rating: 2, comment: 'للأسف الطلب كان ناقص عنصر. وما حدش رد على الشكوى.', orderNumber: '#1021', date: '2026-03-20T08:00:00', helpful: 3 },
  { id: 8, customer: 'نورا خالد', rating: 5, comment: 'كل مرة أطلب من عندكم بيكون الأكل والمشروبات رائعة! شكراً 🌟', orderNumber: '#1020', date: '2026-03-19T22:15:00', helpful: 18 },
];

const ratingDistribution = [
  { stars: 5, count: 145, percentage: 62 },
  { stars: 4, count: 55, percentage: 23 },
  { stars: 3, count: 20, percentage: 9 },
  { stars: 2, count: 10, percentage: 4 },
  { stars: 1, count: 4, percentage: 2 },
];

export default function CafeReviews() {
  const { formatDateTime, language } = useTranslation();
  const [filterRating, setFilterRating] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [replyingTo, setReplyingTo] = useState<number | null>(null);
  const [replyText, setReplyText] = useState('');

  const filteredReviews = mockReviews.filter((review) => {
    const matchesRating = !filterRating || review.rating === filterRating;
    const matchesSearch = !searchQuery ||
      review.customer.includes(searchQuery) ||
      review.comment.includes(searchQuery);
    return matchesRating && matchesSearch;
  });

  const avgRating = 4.8;
  const totalReviews = 234;
  const positivePercent = 85;

  const renderStars = (rating: number, size = 14) => (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          size={size}
          className={star <= rating ? 'text-amber-400 fill-amber-400' : 'text-slate-300 dark:text-slate-600'}
        />
      ))}
    </div>
  );

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-800 dark:text-white">
          {language === 'ar' ? 'التقييمات والمراجعات' : 'Reviews & Ratings'}
        </h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">
          {language === 'ar' ? 'آراء العملاء عن مقهى الورد' : 'Customer feedback for Rose Cafe'}
        </p>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Average Rating */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6 text-center">
          <p className="text-5xl font-bold text-slate-800 dark:text-white mb-2">{avgRating}</p>
          {renderStars(Math.round(avgRating), 24)}
          <p className="text-slate-500 mt-2">
            {language === 'ar' ? `من ${totalReviews} تقييم` : `from ${totalReviews} reviews`}
          </p>
        </div>

        {/* Rating Distribution */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6">
          <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 mb-4">
            {language === 'ar' ? 'توزيع التقييمات' : 'Rating Distribution'}
          </h3>
          <div className="space-y-2.5">
            {ratingDistribution.map((r) => (
              <div key={r.stars} className="flex items-center gap-3">
                <button
                  onClick={() => setFilterRating(filterRating === r.stars ? null : r.stars)}
                  className={`flex items-center gap-1 text-sm min-w-[40px] ${
                    filterRating === r.stars ? 'text-amber-600 font-bold' : 'text-slate-500'
                  }`}
                >
                  {r.stars} <Star size={12} className="text-amber-400 fill-amber-400" />
                </button>
                <div className="flex-1 h-2.5 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-amber-400 rounded-full transition-all"
                    style={{ width: `${r.percentage}%` }}
                  />
                </div>
                <span className="text-xs text-slate-400 min-w-[30px] text-end">{r.count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Stats */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
              <ThumbsUp size={20} className="text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <p className="text-xl font-bold text-slate-800 dark:text-white">{positivePercent}%</p>
              <p className="text-xs text-slate-500">{language === 'ar' ? 'تقييم إيجابي' : 'Positive'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <MessageSquare size={20} className="text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-xl font-bold text-slate-800 dark:text-white">67%</p>
              <p className="text-xs text-slate-500">{language === 'ar' ? 'نسبة الرد' : 'Response Rate'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
              <TrendingUp size={20} className="text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <p className="text-xl font-bold text-slate-800 dark:text-white">+0.3</p>
              <p className="text-xs text-slate-500">{language === 'ar' ? 'تحسن هذا الشهر' : 'Improved this month'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={18} className="absolute start-4 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder={language === 'ar' ? 'بحث في التقييمات...' : 'Search reviews...'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full ps-11 pe-4 py-3 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-200 placeholder:text-slate-400 focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none"
          />
        </div>
        {filterRating && (
          <button
            onClick={() => setFilterRating(null)}
            className="px-4 py-2 rounded-xl bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 font-semibold text-sm flex items-center gap-2"
          >
            <Filter size={14} />
            {filterRating} {language === 'ar' ? 'نجوم' : 'Stars'} ×
          </button>
        )}
      </div>

      {/* Reviews List */}
      <div className="space-y-4">
        {filteredReviews.map((review) => (
          <div
            key={review.id}
            className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-5"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white font-bold">
                  {review.customer[0]}
                </div>
                <div>
                  <p className="font-semibold text-slate-800 dark:text-white">{review.customer}</p>
                  <div className="flex items-center gap-2">
                    {renderStars(review.rating)}
                    <span className="text-xs text-slate-400">{review.orderNumber}</span>
                  </div>
                </div>
              </div>
              <span className="text-xs text-slate-400">{formatDateTime(review.date)}</span>
            </div>

            <p className="text-slate-600 dark:text-slate-300 leading-relaxed">{review.comment}</p>

            {/* Helpful */}
            <div className="flex items-center gap-4 mt-3 text-xs text-slate-400">
              <button className="flex items-center gap-1 hover:text-amber-600 transition-colors">
                <ThumbsUp size={12} />
                {review.helpful} {language === 'ar' ? 'مفيد' : 'helpful'}
              </button>
            </div>

            {/* Reply */}
            {review.reply && (
              <div className="mt-3 ms-6 bg-amber-50 dark:bg-amber-900/10 rounded-xl p-3 border-s-4 border-amber-400">
                <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 mb-1">
                  {language === 'ar' ? 'رد المقهى' : 'Cafe Reply'}
                </p>
                <p className="text-sm text-slate-600 dark:text-slate-300">{review.reply}</p>
              </div>
            )}

            {/* Reply Action */}
            {!review.reply && (
              <div className="mt-3">
                {replyingTo === review.id ? (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder={language === 'ar' ? 'اكتب ردك...' : 'Write your reply...'}
                      className="flex-1 px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700 text-sm text-slate-800 dark:text-slate-200 placeholder:text-slate-400 focus:ring-2 focus:ring-amber-500 outline-none"
                    />
                    <button
                      onClick={() => { setReplyingTo(null); setReplyText(''); }}
                      className="px-3 py-2 rounded-xl text-sm font-semibold bg-slate-100 dark:bg-slate-700 text-slate-500 hover:bg-slate-200 transition-colors"
                    >
                      {language === 'ar' ? 'إلغاء' : 'Cancel'}
                    </button>
                    <button
                      onClick={() => { setReplyingTo(null); setReplyText(''); }}
                      className="px-4 py-2 rounded-xl text-sm font-semibold bg-amber-600 text-white hover:bg-amber-700 transition-colors"
                    >
                      {language === 'ar' ? 'إرسال' : 'Send'}
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setReplyingTo(review.id)}
                    className="flex items-center gap-1 text-sm text-amber-600 dark:text-amber-400 hover:text-amber-700 font-semibold"
                  >
                    <MessageSquare size={14} />
                    {language === 'ar' ? 'رد على التقييم' : 'Reply to review'}
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {filteredReviews.length === 0 && (
        <div className="text-center py-16 bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700">
          <MessageSquare size={48} className="mx-auto text-slate-300 dark:text-slate-600 mb-4" />
          <p className="text-lg font-semibold text-slate-500">
            {language === 'ar' ? 'لا توجد تقييمات' : 'No reviews found'}
          </p>
        </div>
      )}
    </div>
  );
}
